import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ModalDirective, ModalOptions } from 'ngx-bootstrap/modal';
import { FormBuilder, Validators, FormGroup, Form, ControlValueAccessor } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { CustomerWithStatus, Status, StatusEnum, Customer, ControlTypeEnum, Connector, DataBusLicense } from 'src/api/models';
import { CustomerService } from 'src/api/services/customer.service';
import { DataBusService } from 'src/api/services/data-bus.service';
import { CustomersTableKeys, CustomerTableRow } from './customer-list.types';
import { StatusExtended } from 'src/app/models/status-extended';
import { ConnectorService } from 'src/api/services';

interface CustomerForm {
  customerName: string;
  telephone: string;
  address: string;
  customerMessage: string;
  active: boolean;
  domainName: string;
  customerCode: string;
  enableLiveData: boolean;
  allowsAlarms: boolean;

  timeSeriesConnectorId:number;
  timeSeriesConnectionString: string;

  mediaConnectorId:number;
  mediaConnectionString: string;
  mediaContainerName: string;
  mediaRequireSassToken: boolean;
  contentDeliveryNetwork: string;

  enableControl: boolean;
  controlType: ControlTypeEnum;
  
  eventHubConnectionString: string;
  eventHubName: string;
  
  dataBusAddress: string;
  webHookDataSourceUri: string;
  webHookApiKey: string;

  centreMapLatitude: number;
  centreMapLongitude: number;
  zoomLevel: number;
}

@Component({
  selector: 'app-customer-list',
  templateUrl: './customer-list.component.html',
  styleUrls: ['./customer-list.component.scss']
})
export class CustomerListComponent implements OnInit {
  @ViewChild('customerModal', { static: false }) customerModal: ModalDirective;

  public clicenses?: CustomerWithStatus[]; 
  public customers?: CustomerWithStatus[]; 
  public customersFormatted?: Array<CustomerTableRow>;
  public customersAlerts?: Array<StatusExtended>;
  public connectors?: Connector[];
  public mconnectors?: Connector[];
  public licenses?: DataBusLicense[];

  public isSubmitting = false;
  public isUpdatingCustomer = false;
  public customerToUpdate?: CustomerWithStatus;

  public includeArchived = false;

  public readonly customerTableKeys = CustomersTableKeys;
  public readonly customerGroupModel: FormGroup = this.formBuilder.group({
    customerName: ['', Validators.required],
    telephone: [''],
    address: [''] ,
    customerMessage: [''],
    domainName: ['',  Validators.required],
    customerCode: ['', Validators.maxLength(12)],
    active: [true],
    enableLiveData: [false],
    allowsAlarms: [true],

    timeSeriesConnectorId: [1],
    timeSeriesConnectionString: [''],

    mediaConnectorId: [2],
    mediaConnectionString: [''],
    mediaContainerName: ['Videos'],
    mediaRequireSassToken: [true],

    enableControl: [false],
    controlType: [''],
    contentDeliveryNetwork: [''],
    eventHubConnectionString: [''],
    eventHubName: [''],
    dataBusAddress: [''],
    webHookDataSourceUri: [''],
    webHookApiKey: [''],

    centreMapLatitude: [-42.026167],
    centreMapLongitude: [146.708500],
    zoomLevel: [6],
  });

  onCheckboxChange(e: any) {
    this.getAllCustomers();
  } 

  get customerGroupValue(): CustomerForm {
    return this.customerGroupModel.value;
  }

  get activeLicensedCustomers(): Array<{customer: CustomerWithStatus, license: DataBusLicense}> {
    if (!this.customers || !this.licenses) return [];
    
    return this.customers
      .filter(c => c.active)
      .map(customer => {
        const license = this.licenses!.find(l => l.customerId === customer.customerId);
        return { customer, license: license! };
      })
      .filter(item => item.license != null);
  }

  private modalConfig: ModalOptions = {
    backdrop: true,
    ignoreBackdropClick: true,
    keyboard: false
  }

  constructor(
    private customerService: CustomerService,
    private connectorService: ConnectorService,
    private dataBusService: DataBusService,
    public configService: ApplicationConfigService,
    private router: Router,
    private formBuilder: FormBuilder,
    private toasterService: ToastrService) {}

  ngOnInit(): void {
    this.getAllCustomers();
    this.getAllLicenses();
    this.connectorService.ConnectorGet().subscribe(c => {
      this.connectors = c.filter(con => con.timeSeriesApiType != null);
      this.mconnectors = c.filter(con => con.mediaApiType != null);
      console.log("Connectors", this.connectors);
    });
    this.customerService.CustomerGetCustomerLicenses().subscribe(licenses => {
      this.clicenses = licenses;
      console.log("Customer Licenses", this.clicenses);
    }, err => {
      console.error('ERROR: getCustomerLicenses', err)
    });
  }

  getTotalLicensePrice(): number {
    if (!this.clicenses) return 0;
    //return this.activeLicensedCustomers.reduce((total, item) => total + (item.license.totalPrice || 0), 0);
    // gp through the activeLicensedCustomers and sum the totalPrice
    let total = 0;
    this.clicenses.forEach(item => {
      item.licenses.forEach(lic => {
        total += lic.price || 0;
      });
    });
    return total;
  }

  getMonthlyAmountsDue(): Array<{month: string, amount: number}> {
    if (!this.clicenses) return [];
    
    const monthlyTotals: {[key: string]: number} = {};
    const currentYear = new Date().getFullYear();
    
    // Initialize all months with 0
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    monthNames.forEach(month => {
      monthlyTotals[month] = 0;
    });
    
    // Calculate totals for each month based on expiredDate
    this.clicenses.forEach(customer => {
      customer.licenses.forEach(license => {
        if (license.expiredDate) {
          const expiryDate = new Date(license.expiredDate);
          const expiryYear = expiryDate.getFullYear();
          
          // Only include licenses expiring in the current year
          //if (expiryYear === currentYear) {
            const monthName = monthNames[expiryDate.getMonth()];
            monthlyTotals[monthName] += license.price || 0;
          //}
        }
      });
    });
    
    // Convert to array format
    return monthNames.map(month => ({
      month: month,
      amount: monthlyTotals[month]
    }));
  }

  getCurrentYear(): number {
    return new Date().getFullYear();
  }

  getAllCustomers(): void {
    this.customerService.CustomerGetCustomersWithStatus(this.includeArchived)
      .subscribe(
        res => {
          this.customers = res;
          this.formatCustomers(this.customers);
          this.formatAlerts(this.customers);
        },
        err => {
          console.error('ERROR: getAllCustomers', err)
        }
      )
  }

  getAllLicenses(): void {
    this.dataBusService.DataBusGetCustomerLicenses()
      .subscribe(
        res => {
          this.licenses = res;
        },
        err => {
          console.error('ERROR: getAllLicenses', err)
        }
      )
  }

  /**
   * Redirect to specific customer view
   * @param formattedCustomer
   */
  selectCustomer(formattedCustomer: CustomerTableRow): void {
    this.router.navigate(['/customers', formattedCustomer.id]);
  }
  
  /**
   * Formats customers data to be used in the table, primarily for status display
   */
  formatCustomers(customers: Array<CustomerWithStatus>): void {
    this.customersFormatted = customers.map(customer => {
      let highestStatus = customer.statusList[0] || {} as Status;
      let alertCount = 0;

      customer.statusList.forEach(status => {
        if (status.level < highestStatus.level) {
          highestStatus = status;
        }

        if (status.level < 2) {
          alertCount++;
        }
      })

      if (customer.active){
        return {
          id: customer.customerId,
          name: customer.customerName,
          active: customer.active,
          status: {
            level: !isNaN(highestStatus.level) ? highestStatus.level : 2,
            type: 0,
            message: `${alertCount} Alerts`
          }
        };
      } else {
        return {
          id: customer.customerId,
          name: customer.customerName,
          active: customer.active,
          status: {
            level: 3,
            type: 0,
            message: ''
          }
        };
      }
    })
  }

  /**
   * Combine all customer status into one array to display in Array component
   * @param customers  
   */
  formatAlerts(customers: Array<CustomerWithStatus>): void {
    const alerts = new Array<StatusExtended>();

    customers.forEach(customer => {
      if (customer.active){
        // alerts.push(...customer.statusList);
        customer.statusList.forEach(status => {
          const extended: StatusExtended = {
            level: status.level,
            type: status.type,
            message: `${customer.customerName}'s ${status.message.toLowerCase()}.`,
            url: `${customer.customerId}`
          }

          alerts.push(extended);
        });
      }
    })

    this.customersAlerts = alerts.sort((a, b) => a.level - b.level);
  }

  /**
   * Open the customer modal
   */
  openCustomerModal(): void {
    this.customerModal.config = this.modalConfig;
    this.customerModal.show();
  }

  /**
   * Close the customer modal, resets create/edit logic.
   */  
  closeCustomerModal(): void {
    this.customerModal.hide();
    this.customerGroupModel.reset();
    this.isUpdatingCustomer = false;
    this.customerToUpdate = null;
  }

  /**
   * Depending on Form 'Mode' either call to create or edit a customer
   */
  saveCustomer(): void {
    if (this.isUpdatingCustomer) {
      this.updateCustomer();
    } else {
      this.createCustomer();
    }
  }

  /**
   * Enables the Form to edit a specific customer
   * @param formattedCustomer 
   */
  beginUpdateCustomer(formattedCustomer: CustomerTableRow): void {
    let customer = this.customers.find(customer => customer.customerId === formattedCustomer.id);
    this.customerToUpdate = customer;
    this.customerGroupModel.setValue({
      customerName: customer.customerName,
      telephone: customer.telephone,
      address: customer.address,
      customerMessage: customer.customerMessage,
      domainName: customer.domainName,
      customerCode: customer.customerCode,
      active: customer.active,
      enableLiveData: customer.enableLiveData,
      allowsAlarms: customer.allowsAlarms,
      //times: customer.timeSeriesApiType,
      timeSeriesConnectorId: customer.timeSeriesConnectorId,
      timeSeriesConnectionString: customer.timeSeriesConnectionString,
      mediaConnectorId: customer.mediaConnectorId,
      mediaConnectionString: customer.mediaConnectionString,
      mediaContainerName: customer.mediaContainerName,
      mediaRequireSassToken: customer.mediaRequireSassToken,
      contentDeliveryNetwork: customer.contentDeliveryNetwork,
      enableControl: customer.enableControl,
      controlType: customer.controlType,
      eventHubConnectionString: customer.eventHubConnectionString,
      eventHubName: customer.eventHubName,
      dataBusAddress: customer.dataBusAddress,
      webHookApiKey: customer.webHookApiKey,
      webHookDataSourceUri: customer.webHookDataSourceUri,
      centreMapLatitude: customer.centreMapLatitude,
      centreMapLongitude: customer.centreMapLongitude,
      zoomLevel: customer.zoomLevel,
    });
    

    this.isUpdatingCustomer = true;
    this.openCustomerModal();
  }

  /**
   * Update specific customer, using the customerToUpdate and form values, updates customers array on success
   */
  updateCustomer(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const args: Customer = {
      customerName: this.customerGroupValue.customerName,
      telephone: this.customerGroupValue.telephone,
      address: this.customerGroupValue.address,
      customerMessage: this.customerGroupValue.customerMessage,
      domainName: this.customerGroupValue.domainName,
      customerId: this.customerToUpdate.customerId,
      active: this.customerGroupValue.active,
      enableLiveData: this.customerGroupValue.enableLiveData,
      allowsAlarms:this.customerGroupValue.allowsAlarms,
      customerCode: this.customerGroupValue.customerCode,
      mediaConnectorId: this.customerGroupValue.mediaConnectorId,
      mediaConnectionString: this.customerGroupValue.mediaConnectionString,
      mediaContainerName: this.customerGroupValue.mediaContainerName,
      mediaRequireSassToken: this.customerGroupValue.mediaRequireSassToken,
      contentDeliveryNetwork: this.customerGroupValue.contentDeliveryNetwork,
      timeSeriesConnectorId: this.customerGroupValue.timeSeriesConnectorId,
      timeSeriesConnectionString: this.customerGroupValue.timeSeriesConnectionString,
      enableControl: this.customerGroupValue.enableControl,
      controlType: this.customerGroupValue.controlType,
      eventHubConnectionString: this.customerGroupValue.eventHubConnectionString,
      eventHubName: this.customerGroupValue.eventHubName,
      dataBusAddress: this.customerGroupValue.dataBusAddress,
      webHookApiKey: this.customerGroupValue.webHookApiKey,
      webHookDataSourceUri: this.customerGroupValue.webHookDataSourceUri,
      zoomLevel: this.customerGroupValue.zoomLevel,
      centreMapLatitude: this.customerGroupValue.centreMapLatitude,
      centreMapLongitude: this.customerGroupValue.centreMapLongitude
    } as Customer;

    

    this.customerService.CustomerUpdateCustomer(args)
      .subscribe(
        res => {
          if (res) {
            this.getAllCustomers();
            this.closeCustomerModal();
            this.toasterService.success(`Successfully Updated Customer: ${args.customerName || '-'}`, "Update Customer");
          } else {
            this.toasterService.warning(`Customer Must Be Unique`, "Update Customer");
          }

          this.isSubmitting = false;
        },
        err => {
          console.error("ERROR: updateCustomer", err);
          this.toasterService.error(`Failed to Update Customer: ${args.customerName || '-'}`, "Update Customer");
          this.isSubmitting = false;
        }
      )
  }

  /**
   * Create customer, using the form values, updates customers array on success
   */
  createCustomer(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const args: Customer = {
      customerName: this.customerGroupValue.customerName,
      customerMessage: this.customerGroupValue.customerMessage, 
      telephone: this.customerGroupValue.telephone,
      address: this.customerGroupValue.address,
      active: this.customerGroupValue.active,
      domainName: this.customerGroupValue.domainName,
      allowsAlarms: this.customerGroupValue.allowsAlarms,

      timeSeriesConnectorId: this.customerGroupValue.timeSeriesConnectorId,
      timeSeriesConnectionString: this.customerGroupValue.timeSeriesConnectionString,
      
      mediaConnectorId: this.customerGroupValue.mediaConnectorId,
      mediaConnectionString: this.customerGroupValue.mediaConnectionString,
      mediaContainerName: this.customerGroupValue.mediaContainerName,
      mediaRequireSassToken: this.customerGroupValue.mediaRequireSassToken,
      contentDeliveryNetwork: this.customerGroupValue.contentDeliveryNetwork,
      
      enableControl: this.customerGroupValue.enableControl,
      controlType: this.customerGroupValue.controlType,
      eventHubConnectionString: this.customerGroupValue.eventHubConnectionString,
      eventHubName: this.customerGroupValue.eventHubName,
      dataBusAddress: this.customerGroupValue.dataBusAddress,
      webHookApiKey: this.customerGroupValue.webHookApiKey,
      webHookDataSourceUri: this.customerGroupValue.webHookDataSourceUri,
      
      customerId: 0,
      
      enableLiveData: this.customerGroupValue.enableLiveData,
      customerCode: this.customerGroupValue.customerCode,
      centreMapLatitude: this.customerGroupValue.centreMapLatitude,
      centreMapLongitude: this.customerGroupValue.centreMapLongitude,
      zoomLevel: this.customerGroupValue.zoomLevel
    };

    this.customerService.CustomerCreateCustomer(args)
      .subscribe(
        res => {
          if (res) {
            this.getAllCustomers();
            this.closeCustomerModal();
            this.toasterService.success(`Successfully Created Customer: ${args.customerName || '-'}`, "Create Customer");
          } else {
            this.toasterService.warning(`Customer Must Be Unique`, "Create Customer");
          }

          this.isSubmitting = false;
        },
        err => {
          console.error("ERROR: createCustomer", err);
          this.toasterService.error(`Failed To Create Customer: ${args.customerName || '-'}`, "Create Customer");
          this.isSubmitting = false;
        }
      )
  }

  /**
   * Delete specific customer by ID, updates customers array on success
   * @param formattedCustomer
   */
  deleteCustomer(formattedCustomer: CustomerTableRow): void {
    this.customerService.CustomerDeleteCustomerById(formattedCustomer.id)
      .subscribe(
        res => {
          this.getAllCustomers();
          this.toasterService.success(`Successfully Deleted Customer: ${formattedCustomer.name || '-'}`, "Delete Customer")
        },
        err => {
          console.error("ERROR: deleteCustomer", err);
          this.toasterService.error(`Failed To Delete Customer: ${formattedCustomer.name || '-'}`, "Delete Customer")
        }
      )
  }

  /**
   * Archive customer (soft delete)
   */
  archiveCustomer(): void {
    if (!this.customerToUpdate) return;
    
    const customerName = this.customerToUpdate.customerName;
    
    this.customerService.CustomerDeleteCustomerById(this.customerToUpdate.customerId)
      .subscribe(
        res => {
          this.getAllCustomers();
          this.closeCustomerModal();
          this.toasterService.success(`Successfully Archived Customer: ${customerName || '-'}`, "Archive Customer");
        },
        err => {
          console.error("ERROR: archiveCustomer", err);
          this.toasterService.error(`Failed To Archive Customer: ${customerName || '-'}`, "Archive Customer");
        }
      )
  }

  /**
   * Unarchive customer (restore)
   */
  unarchiveCustomer(): void {
    if (!this.customerToUpdate) return;
    
    const customerName = this.customerToUpdate.customerName;
    
    this.customerService.CustomerUnarchiveCustomer(this.customerToUpdate.customerId)
      .subscribe(
        res => {
          this.getAllCustomers();
          this.closeCustomerModal();
          this.toasterService.success(`Successfully Unarchived Customer: ${customerName || '-'}`, "Unarchive Customer");
        },
        err => {
          console.error("ERROR: unarchiveCustomer", err);
          this.toasterService.error(`Failed To Unarchive Customer: ${customerName || '-'}`, "Unarchive Customer");
        }
      )
  }
}
